/* eslint-disable @typescript-eslint/no-unused-vars */
import boxx from '@baidu/boxx-fe';
import {json2query} from '@baidu/xbox/src/json2query';
import {getCommonParams} from '@lib/utils/native';
import {
    addCommonParams,
    inNewApp,
} from '@lib/utils/tools';
import axios from 'axios';
import __INSTANCE__ from '../InstanceManager';
import {perfLog} from '../log';
import paris from './paris';
import xaf from './xaf';
import getXBdAlive from './chatToken';
import {isPcClient} from './env';
import {MockSSE, SSE} from './sse';
import {Chunk} from '../constants/mock';

interface HttpGetParams {
    url: string;
    abortController?: AbortController;
    chatToken?: string;
}
interface NaHttpResponse<T> {
    status?: number;
    message?: string;
    data: T;
}
interface DataOrHeader {
    [key: string]: any;
}

// 使用axios发送请求，需要手动拼接公参
// 在请求发送前拦截并修改 URL
axios.interceptors.request.use(async config => {
    if (!window.__commonParams) {
        await getCommonParams();
    }
    config.url = addCommonParams(config.url as string);
    return config;
});
// 请求超时默认10s
axios.defaults.timeout = 10 * 1000;

axios.interceptors.response.use(
    response => {
        return response;
    },
    error => {
        // @ts-ignore
        return Promise.reject(new Error(`response error error=${error}`, {
            cause: axios.isCancel(error) ? {
                message: 'Operation canceled by the user',
            } : error.response,
        }));
    }
);

/**
 * 调用 POST 接口
 * @param url URL, 注意 URL中需要事先拼接好 URL 参数
 * @param data 需要post 的数据
 * @param cache '0', 告知客户端是否缓存返回数据，0不缓存/1缓存，仅安卓有
 * @param refresh '1' 是否强制刷新，0不使用/1使用，仅安卓有
 * @param pu '1' 是否携带公共参数，0不携带/1携带
 * @param header 请求头部信息，要追加到 HTTP 请求的 HTTP 请求标头中。
 * @param isJson2query 是否需要参数编码，true是需要，默认需要编码
 */
export const httpPost = <T>(
    url: string,
    data: DataOrHeader,
    option?: any,
    isJson2query = true,
    headers: DataOrHeader = {},
    pu = '1',
    refresh = '1',
    cache = '0'
): Promise<NaHttpResponse<T>> => {
    // if (boxx.canIUse('newapp.net.request')) {
    //     return new Promise((resolve, reject) => {
    //         // 使用端能力发送请求，会自动带上公参
    //         boxx.newapp.net.request({
    //             url,
    //             data,
    //             method: 'post',
    //             pu,
    //             refresh,
    //             cache,
    //             header: headers,
    //             success: (res: NaHttpResponse<T>) => {
    //                 resolve(res);
    //             },
    //             fail: (res: NaHttpResponse<T>) => {
    //                 console.error('调用端能力获取数据失败');
    //                 reject(res);
    //             },
    //         });
    //     });
    // }
    const startTime = new Date().getTime();
    const dim = {
        status: 0,
        errno: 0,
        path: new URL(url).pathname,
        type: 'post',
    };
    // 有askContent的话headers带X-Bd-Alive(目前仅有getRecoverData\completionsStop场景)
    if (data?.serverToken) {
        headers['X-Bd-Alive'] = data.serverToken;
        delete data.serverToken;
    }
    else if (data?.askContent) {
        headers['X-Bd-Alive'] = getXBdAlive(data?.askContent as string);
        delete data.askContent;
    }

    // 端上是否拦截接口请求
    const interceptReq = inNewApp && +data?.nb_ict === 10;

    return new Promise(async (resolve, reject) => {
        axios({
            method: 'post',
            url,
            data: isJson2query ? json2query(data) : data,
            headers: {
                ...headers,
                'Acs-Token': !interceptReq ? await paris.getAcsToken() as string : '',
            },
            withCredentials: true,
            ...option,
        }).then(res => {
            dim.status = res.status;
            // @ts-ignore
            dim.errno = res.data?.errno;
            if (+res.status === 200) {
                resolve(res);
            }
            else {
                reject(res);
            }
        }).catch(err => {
            dim.status = err?.cause?.status;
            reject(err);
        }).finally(() => {
            // 接口性能打点，接口量太大，抽样 5%
            if (Math.random() < 0.05) {
                const perfTime = new Date().getTime() - startTime;
                const perfParms = {
                    time: perfTime,
                };

                perfLog('api', perfParms, {
                    ...dim,
                });
            }
        });
    });
};

/**
 * 调用 GET 接口
 * @param url URL, 注意 URL中需要事先拼接好 URL 参数
 */
export const httpGet = <T>(params: HttpGetParams): Promise<NaHttpResponse<T>> => {
    const {url, abortController} = params;
    const startTime = new Date().getTime();
    const dim = {
        status: 0,
        errno: 0,
        path: new URL(url).pathname,
        type: 'get',
    };

    // 端上是否拦截接口请求
    const interceptReq = inNewApp && url.includes('nb_ict=10');

    return new Promise(async (resolve, reject) => {
        axios({
            method: 'get',
            url,
            headers: {
                'Acs-Token': !interceptReq ? await paris.getAcsToken() as string : '',
            },
            signal: abortController?.signal,
            withCredentials: true,
        }).then(res => {
            dim.status = res.status;
            // @ts-ignore
            dim.errno = res.data?.errno;
            if (+res.status === 200) {
                resolve(res);
            }
            else {
                reject(res);
            }
        }).catch(err => {
            dim.status = err.cause?.response?.status;
            reject(err);
        }).finally(() => {
            // 接口性能打点，接口量太大，抽样 5%
            if (Math.random() < 0.05) {
                const perfTime = new Date().getTime() - startTime;
                const perfParms = {
                    time: perfTime,
                };

                perfLog('api', perfParms, {
                    ...dim,
                });
            }
        });
    });

};

export const nativePost = <T>(url: string, data: DataOrHeader, headers: DataOrHeader = {},
    pu = '1', refresh = '1', cache = '0') => {
    return new Promise((resolve, reject) => {
        if (boxx.canIUse('newapp.net.request')) {
            // 使用端能力发送请求，会自动带上公参
            boxx.newapp.net.request({
                url,
                data,
                method: 'post',
                pu,
                refresh,
                cache,
                header: headers,
                success: (res: NaHttpResponse<T>) => {
                    resolve(res);
                },
                fail: (res: NaHttpResponse<T>) => {
                    console.error('调用端能力获取数据失败');
                    reject(res);
                },
            });
        }
        else {
            reject(false);
            console.error('newapp.net.request 端能力不存在');
        }
    });
};

/**
 * @param chatType 会话类型 normal-正常对话 prompt-prompt对话 bot-bot对话
 * @param body post请求携带的参数
 * @returns SSE实例eventSource 通过使用eventSource.addEventListener进行数据处理
 */
export const createSSEInstance = async <T>(
    api: string,
    body: DataOrHeader,
    timeout?: object | null
) => {
    const [
        jt = '',
        token = '',
    ] = await Promise.all([
        xaf(),
        paris.getAcsToken(),
    ]).catch(() => []);

    const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Accept': 'text/event-stream',
        'Acs-Token': token as string,
    };

    // SSE接口有content的话header带X-Bd-Alive
    if (body?.content) {
        headers['X-Bd-Alive'] = getXBdAlive(body.content as string);
    }

    const API = __INSTANCE__.config?.api?.sse || api;
    body.model = __INSTANCE__.config?.api?.model || body.model;

    if (__INSTANCE__.config?.api?.sseParams) {
        Object.assign(body, __INSTANCE__.config.api.sseParams);
    }

    const options = {
        payload: JSON.stringify(body),
        withCredentials: true,
        headers,
    };
    if (timeout) {
        // @ts-ignore
        options.timeout = timeout;
    }

    // pc的话，不拼接公共参数，会在pc的请求拦截里统一加
    const eventSource = new SSE(`${API}?${isPcClient() ? '' : window.__commonParams + '&'}jt=${jt}`, options);
    // 开始发请求
    eventSource.stream();
    // jt 要求 不缓存公参 zid，重新拉取公参
    getCommonParams();
    return eventSource;
};
/**
 * @param options 假对话上屏信息
 * @returns MockSSE实例
 */
export const createMockSSEInstance = async (chunks: Chunk[]) => {
    const es = new MockSSE(chunks);
    es.stream();
    return es;
};
